import { usePostHog } from "posthog-js/react";
import { useCallback } from "react";

export interface ChatSessionEventProperties {
  chatType: "text" | "voice";
  agentType?: string;
  sessionId?: string;
}

export interface VoiceAgentEventProperties {
  agentId: string;
  agentType?: string;
  conversationId?: string;
}

/**
 * Custom hook for tracking PostHog analytics events
 */
export function useAnalytics() {
  const posthog = usePostHog();

  const trackChatSessionStart = useCallback(
    (properties: ChatSessionEventProperties) => {
      posthog?.capture("chat_session_started", properties);
    },
    [posthog]
  );

  const trackChatSessionEnd = useCallback(
    (properties: ChatSessionEventProperties & { duration?: number }) => {
      posthog?.capture("chat_session_ended", properties);
    },
    [posthog]
  );

  const trackVoiceAgentStart = useCallback(
    (properties: VoiceAgentEventProperties) => {
      posthog?.capture("voice_agent_started", properties);
    },
    [posthog]
  );

  const trackVoiceAgentStop = useCallback(
    (properties: VoiceAgentEventProperties & { duration?: number }) => {
      posthog?.capture("voice_agent_stopped", properties);
    },
    [posthog]
  );

  const trackPageView = useCallback(
    (pageName: string, properties?: Record<string, any>) => {
      posthog?.capture("$pageview", { page: pageName, ...properties });
    },
    [posthog]
  );

  const identifyUser = useCallback(
    (userId: string, properties?: Record<string, any>) => {
      posthog?.identify(userId, properties);
    },
    [posthog]
  );

  const reset = useCallback(() => {
    posthog?.reset();
  }, [posthog]);

  return {
    trackChatSessionStart,
    trackChatSessionEnd,
    trackVoiceAgentStart,
    trackVoiceAgentStop,
    trackPageView,
    identifyUser,
    reset,
  };
}
